import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { 
  TextField, Select, MenuItem, FormControl, InputLabel, 
  Button, Stack, Modal, Typography, Box 
} from "@mui/material";
import { 
  getUserEmail, getUserName, clearSignInTime, 
  clearUserEmail, clearUserName, clearIsAdmin, getIsAdmin 
} from "../utils/sessionCheck";
import { getDocs, collection, query, where } from "firebase/firestore";
import { firestore } from "../firebase.config";
import PageNavBar from '../components/PageNavBar';
import Footer from "../components/Footer";
import { useUserContext } from "../context/UserContext";
import TenantBanner from "../components/TenantBanner";
import { getQREventById } from "../context/Api";
import ReactECharts from "echarts-for-react";


const Report = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(""); 
  const [userList, setUserList] = useState([]); 
  const [csvPreview, setCsvPreview] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [chartData, setChartData] = useState([]);
  const [chartTitle, setChartTitle] = useState("");
  const [chartOrientation, setChartOrientation] = useState("vertical");
  const [selectedUserOption, setSelectedUserOption] = useState("");
  const uEmail = getUserEmail();
  const uName = getUserName();
  const isAdmin = getIsAdmin();
  const { userRole, tenantId, selectedTenantId } = useUserContext();
  const [tenantFilterID, setTenantFilterID] = useState(null);
  const [eventId, setEventId] = useState("");

  //for team changes
  const [team, setTeam] = useState("");
  const [teamList, setTeamList] = useState([]);

  // Set tenant ID once on component mount
  useEffect(() => {
    const id = userRole === "SuperAdmin" 
      ? selectedTenantId || sessionStorage.getItem("selectedTenantId") 
      : tenantId || sessionStorage.getItem("selectedTenantId");
    setTenantFilterID(id);
  }, [userRole, tenantId, selectedTenantId]);

  const formatInputDate = (date) => {
    if (!date || Number.isNaN(date.getTime())) return "";
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().split("T")[0];
  };

  // Convert DD-MM-YY format to Date object for proper comparison
  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    try {
      // Handle both string and number formats, trim whitespace
      const cleaned = String(dateStr).trim();
      const parts = cleaned.split('-');
      if (parts.length !== 3) return null;
      
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      let year = parseInt(parts[2], 10);
      
      // Check for NaN values
      if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
      
      // Validate day and month ranges (day can be 1-31, month 1-12)
      if (day < 1 || day > 31 || month < 1 || month > 12) return null;
      
      // Handle 2-digit year: assume 2000-2099 range
      // Also handle 4-digit years if they exist
      const fullYear = year < 100 ? 2000 + year : year;
      
      // Create date object (using local time to avoid timezone issues)
      const date = new Date(fullYear, month - 1, day);
      
      // Check if date is valid (handles invalid dates like Feb 30)
      if (isNaN(date.getTime())) return null;
      
      // Verify the parsed date matches the input (catches invalid dates)
      // Note: We use getMonth() which returns 0-11, so we compare with month - 1
      if (date.getFullYear() !== fullYear || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
      }
      
      return date;
    } catch (error) {
      console.error(`[parseDateString] Error parsing date: ${dateStr}`, error);
      return null;
    }
  };

  // Check if a date string (DD-MM-YY) falls within the range
  const isDateInRange = (dateStr, startDateObj, endDateObj) => {
    if (!dateStr || !startDateObj || !endDateObj) return false;
    
    const dateObj = parseDateString(dateStr);
    if (!dateObj) {
      return false;
    }
    
    // Create new Date objects to avoid mutating the originals
    // Use local date components to avoid timezone issues
    const start = new Date(
      startDateObj.getFullYear(),
      startDateObj.getMonth(),
      startDateObj.getDate(),
      0, 0, 0, 0
    );
    
    const end = new Date(
      endDateObj.getFullYear(),
      endDateObj.getMonth(),
      endDateObj.getDate(),
      23, 59, 59, 999
    );
    
    const check = new Date(
      dateObj.getFullYear(),
      dateObj.getMonth(),
      dateObj.getDate(),
      0, 0, 0, 0
    );
    
    // Debug logging for June dates
    if (dateObj.getMonth() === 5) { // June is month 5 (0-indexed)
      console.log(`[isDateInRange] June date: ${dateStr} -> ${check.toISOString().split('T')[0]}, range: ${start.toISOString().split('T')[0]} to ${end.toISOString().split('T')[0]}, inRange: ${check >= start && check <= end}`);
    }
    
    return check >= start && check <= end;
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "Unknown Date";
    const [day, month, year] = dateStr.split("-");
    const dateObj = new Date(2000 + parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    if (Number.isNaN(dateObj.getTime())) return dateStr;
    return dateObj.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
  };

  const handleDateChange = (setter) => (event) => {
    setter(event.target.value);
  };

  // Set default date range (last 1 week) on component mount
  useEffect(() => {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    const start = new Date(end);
    start.setDate(end.getDate() - 6); // Last 7 days (including today)
    setStartDate(formatInputDate(start));
    setEndDate(formatInputDate(end));
  }, []);

  // Utility functions
  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const timeLower = timeStr.toString().toLowerCase();
    const isPM = timeLower.includes('pm');
    const timeWithoutPeriod = timeStr.replace(/am|pm/gi, '').trim();
    const parts = timeWithoutPeriod.split(':');
    if (parts.length < 2) return 0;

    let hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    const seconds = parts.length > 2 ? (parseInt(parts[2]) || 0) : 0;

    if (isPM && hours < 12) hours += 12;
    if (!isPM && hours === 12) hours = 0;

    return hours * 60 + minutes + seconds / 60;
  };

  const calculateDuration = (startTime, endTime) => {
    const startMinutes = parseTimeToMinutes(startTime);
    const endMinutes = parseTimeToMinutes(endTime);

    if (isNaN(startMinutes)) return 0;
    if (isNaN(endMinutes)) return 0;

    return endMinutes < startMinutes 
      ? (24 * 60 - startMinutes) + endMinutes
      : endMinutes - startMinutes;
  };

  const formatTimeString = (timeStr) => {
    if (!timeStr) return '';
    if (typeof timeStr === 'string' && timeStr.match(/^\d{1,2}:\d{2}:\d{2}/)) {
      return timeStr;
    }

    const minutes = parseTimeToMinutes(timeStr);
    if (isNaN(minutes)) return '00:00:00';

    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    const secs = Math.floor((minutes % 1) * 60);

    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatMonthYear = (dateStr) => {
    const [day, month, year] = dateStr.split('-');
    const dateObj = new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
    return dateObj.toLocaleString('default', { month: 'short' }) + '-' + year;
  };

  // Event handlers

  const handleChange = (event) => setUser(event.target.value);

  // Fetch users once on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      if (!tenantFilterID) return;

      try {
        // ✅ Fetch Users
        const userQuery = query(
          collection(firestore, "Users"),
          where("Is_Active", "==", true),
          where("tenantId", "==", tenantFilterID)
        );
      
        const userSnapshot = await getDocs(userQuery);
      
        // ✅ Fetch Groups (Teams)
        const groupQuery = query(
          collection(firestore, "Groups"),
          where("tenantId", "==", tenantFilterID)
        );
      
        const groupSnapshot = await getDocs(groupQuery);
      
        // 🔥 Create teamId → teamName map
        const teamMap = {};
        groupSnapshot.docs.forEach(doc => {
          const data = doc.data();

          console.log("GROUP:", doc.id, data); // 🔥 keep this for debug

          teamMap[doc.id] = data.teamName;  // ✅ FIX
        });
      
        let usersList = [];
        let teamsMap = {};
      
        userSnapshot.docs.forEach(doc => {
          const data = doc.data();
        
          const { userName, userEmail, teamIds = [] } = data;
        
          usersList.push({
            name: userName,
            email: userEmail,
            teamIds
          });
        
          // 🔥 Use teamMap instead of teamNames
          teamIds.forEach((id) => {
            if (!teamsMap[id]) {
              teamsMap[id] = {
                id: id,
                name: teamMap[id] || `Team ${id}` // ✅ FIXED
              };
            }
          });
        });
      
        setUserList(usersList);
        setTeamList(Object.values(teamsMap));
      
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [tenantFilterID, userRole, uEmail, uName]);

  useEffect(() => {
    // For admins and SuperAdmins, default to "all"
    // For supervisors, default to themselves (first in list)
    // For regular users, already set to their email
    if ((userRole === "admin" || userRole === "SuperAdmin") && userList.length && !user) {
      setUser("all");
    } else if (userRole !== "admin" && userRole !== "SuperAdmin" && userRole !== "user" && userList.length && !user) {
      // Supervisor - select themselves (first in list, which is their own email)
      setUser(userList[0].email);
    }
  }, [userRole, userList, user]);

  useEffect(() => {
    if (userRole === "user" && uEmail) {
      setSelectedUserOption(uEmail);
    }
  }, [userRole, uEmail]);

  const querySubmit = (event) => {
    event.preventDefault();
    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }

    const selectedUser = userRole === "user" ? uEmail : user;
    if (!selectedUser) {
      alert("Please select a user.");
      return;
    }

    // Convert input dates to Date objects for comparison
    // Parse the date string to avoid timezone issues
    const startParts = startDate.split('-');
    const endParts = endDate.split('-');
    
    // Create dates using local time (year, month-1, day)
    const startDateObj = new Date(
      parseInt(startParts[0], 10), 
      parseInt(startParts[1], 10) - 1, 
      parseInt(startParts[2], 10)
    );
    const endDateObj = new Date(
      parseInt(endParts[0], 10), 
      parseInt(endParts[1], 10) - 1, 
      parseInt(endParts[2], 10)
    );
    
    // Validate date range
    if (startDateObj > endDateObj) {
      alert("Start date must be before or equal to end date.");
      return;
    }

    setSelectedUserOption(selectedUser);
    let teamUsers = [];

    if (team) {
      teamUsers = userList
        .filter(u => u.teamIds?.includes(team))
        .map(u => u.email);
    }

    fetchData(selectedUser, startDateObj, endDateObj, teamUsers);
  };

  const fetchData = async (option, startDateObj, endDateObj,teamUsers = []) => {
    try {
      if (option === "all") {
        await fetchAllData(startDateObj, endDateObj, option, teamUsers);
      } else {
        await fetchByName(option, startDateObj, endDateObj, teamUsers);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchAllData = async (startDateObj, endDateObj, option = "all",teamUsers = []) => {
    try {
      if (!tenantFilterID || userList.length === 0) {
        alert("Tenant data not available");
        return;
      }

      const tenantUserEmails = userList.map(user => user.email);
      
      // For supervisors, only fetch bookings from themselves + supervised users
      let userEmailFilter = null;
      if (userRole !== "admin" && userRole !== "SuperAdmin" && userRole !== "user") {
        // Check if user is a supervisor
        const { getSupervisedUsers } = await import("../context/Api");
        const supervisedUsers = await getSupervisedUsers(uEmail);
        if (supervisedUsers.length > 0) {
          // Include supervisor themselves + supervised users
          userEmailFilter = [
            uEmail, // Supervisor themselves
            ...supervisedUsers.map(u => u.userEmail) // Team members
          ];
        }
      }
      
      // Check if date range includes dates before/on July 2024
      // July 1, 2024 = new Date(2024, 6, 1) (month is 0-indexed, so 6 = July)
      const julyCutoff = new Date(2024, 6, 1); // July 1, 2024
      julyCutoff.setHours(0, 0, 0, 0);
      const includesOldDates = startDateObj <= julyCutoff;
      
      let timerSnapshot;
      if (includesOldDates) {
        // For dates before/on July: fetch all entries (with or without tenantId)
        // We'll filter by tenantId in memory for dates after July
        const q = query(collection(firestore, "Timer"));
        timerSnapshot = await getDocs(q);
      } else {
        // For dates after July: only fetch entries with tenantId
        const q = query(
          collection(firestore, "Timer"),
          where("tenantId", "==", tenantFilterID)
        );
        timerSnapshot = await getDocs(q);
      }
      
      // Filter by date range in memory and only show approved bookings (exclude rejected)
      let docs = timerSnapshot.docs.filter(doc => {
        const data = doc.data();
        const recordDate = data.date;
        const isInDateRange = isDateInRange(recordDate, startDateObj, endDateObj);
        
        if (!isInDateRange) return false;
        
        // For dates after July: require tenantId to match
        const parsedDate = parseDateString(recordDate);
        if (parsedDate && parsedDate > julyCutoff) {
          // After July: must have matching tenantId
          if (data.tenantId !== tenantFilterID) {
            return false;
          }
        }
        // Before/on July: allow entries with or without tenantId
        
        // Only show approved bookings (or bookings without approvalStatus for backward compatibility)
        // Explicitly exclude rejected bookings
        const isTeamUser = teamUsers.length === 0 || teamUsers.includes(data.userEmail);
        const isTeamMatch = !team || team === "all" || data.teamId === team;
        // For supervisors, only show bookings from themselves + supervised users
        // For admins, show all (no filter)
        const isApproved = (!data.approvalStatus || data.approvalStatus === "approved") && data.approvalStatus !== "rejected";
        const isSupervisedUser = !userEmailFilter || userEmailFilter.includes(data.userEmail);
        return isApproved && isSupervisedUser && isTeamUser;
      });

      if (eventId) {
        const ev = await getQREventById(eventId);
        if (ev) {
          const near = (lat, lon) => {
            if (typeof lat !== 'number' || typeof lon !== 'number') return false;
            const dlat = Math.abs(lat - ev.latitude);
            const dlon = Math.abs(lon - ev.longitude);
            return dlat < 0.01 && dlon < 0.01;
          };
          docs = docs.filter(d => {
            const data = d.data();
            return data.qrEventId === ev.id || near(data.latitude, data.longitude);
          });
        }
      }
      
      handleDataResponse(docs, option);
    } catch (error) {
      console.error("Error fetching all data:", error);
    }
  };

  const fetchByName = async (option, startDateObj, endDateObj,teamUsers = []) => {
    try {
      // Check if date range includes dates before/on July 2024
      const julyCutoff = new Date(2024, 6, 1); // July 1, 2024
      julyCutoff.setHours(0, 0, 0, 0);
      const includesOldDates = startDateObj <= julyCutoff;
      
      let querySnapshot;
      if (includesOldDates) {
        // For dates before/on July: fetch all entries for the user (with or without tenantId)
        const q = query(
          collection(firestore, "Timer"),
          where("userEmail", "==", option)
        );
        querySnapshot = await getDocs(q);
      } else {
        // For dates after July: only fetch entries with tenantId
        const q = query(
          collection(firestore, "Timer"),
          where("userEmail", "==", option),
          where("tenantId", "==", tenantFilterID)
        );
        querySnapshot = await getDocs(q);
      }
      
      // Filter by date range in memory and only show approved bookings (exclude rejected)
      const docs = querySnapshot.docs.filter(doc => {
        const data = doc.data();
        const recordDate = data.date;
        const isInDateRange = isDateInRange(recordDate, startDateObj, endDateObj);
        
        if (!isInDateRange) return false;
        
        // For dates after July: require tenantId to match
        const parsedDate = parseDateString(recordDate);
        if (parsedDate && parsedDate > julyCutoff) {
          // After July: must have matching tenantId
          if (data.tenantId !== tenantFilterID) {
            return false;
          }
        }
        // Before/on July: allow entries with or without tenantId
        
        // Only show approved bookings (or bookings without approvalStatus for backward compatibility)
        // Explicitly exclude rejected bookings
        const isTeamUser = teamUsers.length === 0 || teamUsers.includes(data.userEmail);
        const isApproved = (!data.approvalStatus || data.approvalStatus === "approved") && data.approvalStatus !== "rejected";
        
        return isApproved && isTeamUser;
      });
      
      handleDataResponse(docs, option);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleDataResponse = (docs, selectionKey) => {
    if (docs.length === 0) {
      setCsvPreview("No data for selected dates.");
      setShowDownloadButton(false);
      setOpenModal(true);
      setChartData([]);
      return;
    }

    const data = [];
    const userSummary = {};
    const aggregatedTotals = {};
    
    docs.forEach((doc) => {
      const { date, endTime, startTime, taskList, taskDescription, userEmail, userName } = doc.data();
      
      const durationMinutes = calculateDuration(startTime, endTime);
      const durationHours = durationMinutes / 60;
      const monthYear = formatMonthYear(date);
      
      // Use taskDescription if available, otherwise fall back to taskList
      const taskData = taskDescription || taskList;
      const formattedTasks = Array.isArray(taskData) 
        ? taskData.join('\n') 
        : taskData || '';

      data.push({
        "ID": doc.id,
        "Date": date,
        "Month (MMM-yy)": monthYear,
        "Name": userName,
        "Email ID": userEmail,
        "Tasks": formattedTasks,
        "Start Time": formatTimeString(startTime),
        "End Time": formatTimeString(endTime),
        "Total Time worked (in Mins)": durationMinutes.toFixed(2),
        "Total Time worked (in Hrs)": durationHours.toFixed(2),
      });
      
      // Build user summary
      const userKey = `${userEmail}-${monthYear}`;
      if (!userSummary[userKey]) {
        userSummary[userKey] = {
          name: userName,
          email: userEmail,
          month: monthYear,
          totalHours: 0,
          totalMinutes: 0
        };
      }
      
      userSummary[userKey].totalHours += durationHours;
      userSummary[userKey].totalMinutes += durationMinutes;

      const label = selectionKey === "all" 
        ? (userName || userEmail || "Unknown User")
        : formatDisplayDate(date);
      aggregatedTotals[label] = (aggregatedTotals[label] || 0) + durationHours;
    });

    // Convert summary to array
    const summaryData = Object.values(userSummary).map(item => ({
      "Name": item.name,
      "Email ID": item.email,
      "Month (MMM-yy)": item.month,
      "Total Hours": item.totalHours.toFixed(2),
      "Total Minutes": item.totalMinutes.toFixed(2)
    }));

    // Add grand totals
    const grandTotalHours = summaryData.reduce((sum, item) => sum + parseFloat(item["Total Hours"]), 0);
    const grandTotalMinutes = summaryData.reduce((sum, item) => sum + parseFloat(item["Total Minutes"]), 0);
    
    summaryData.push({
      "Name": "GRAND TOTAL",
      "Email ID": "",
      "Month (MMM-yy)": "",
      "Total Hours": grandTotalHours.toFixed(2),
      "Total Minutes": grandTotalMinutes.toFixed(2)
    });

    const csvContent = convertJsonToCsv(data, summaryData);
    setCsvPreview(csvContent);
    setShowDownloadButton(true);
    // setOpenModal(true);  //this will stop my csv popup

    const formattedChartData = Object.entries(aggregatedTotals).map(([label, value]) => ({
      label,
      value: parseFloat(value.toFixed(2)),
    })).sort((a, b) => b.value - a.value);

    setChartData(formattedChartData);
    setChartTitle(selectionKey === "all" ? "Total hours by user" : "Daily hours worked");
    setChartOrientation(formattedChartData.length > 12 ? "horizontal" : "vertical");
  };

  function convertJsonToCsv(transactionData, summaryData) {
    const escapeField = (field) => {
      if (field == null) return '';
      let str = String(field);
      str = str.replace(/"/g, '""');
      return str.includes(',') || str.includes('\n') || str.includes('"') 
        ? `"${str}"` 
        : str;
    };

    const transactionHeaders = [
      "ID", "Date", "Month (MMM-yy)", "Name", "Email ID", "Tasks", 
      "Start Time", "End Time", "Total Time worked (in Mins)", "Total Time worked (in Hrs)"
    ];

    const summaryHeaders = [
      "Name", "Email ID", "Month (MMM-yy)", "Total Hours", "Total Minutes"
    ];

    const transactionRows = transactionData.map(item => 
      transactionHeaders.map(header => escapeField(item[header])).join(',')
    );

    const summaryRows = summaryData.map(item => 
      summaryHeaders.map(header => escapeField(item[header])).join(',')
    );

    return [
      transactionHeaders.join(','),
      ...transactionRows,
      "",
      "Monthly Summary",
      summaryHeaders.join(','),
      ...summaryRows
    ].join('\n');
  }

  function downloadCsv(csvContent) {
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `time_report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  const resolvedUserName = useMemo(() => {
    if (!selectedUserOption || selectedUserOption === "all") return "";
    const match = userList.find((entry) => entry.email === selectedUserOption);
    return match?.name || selectedUserOption;
  }, [selectedUserOption, userList]);

  const chartSubtitle = useMemo(() => {
    if (!selectedUserOption) return "Generate a report to visualize logged hours.";
    return selectedUserOption === "all"
      ? "Summed hours for each user within the selected date range."
      : `Daily hours logged by ${resolvedUserName || "the selected user"}.`;
  }, [selectedUserOption, resolvedUserName]);

  const chartOptions = useMemo(() => {
    if (!chartData.length) return null;
    const labels = chartData.map((item) => item.label);
    const values = chartData.map((item) => item.value);
    const commonConfig = {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const datum = Array.isArray(params) ? params[0] : params;
          if (!datum) return "";
          return `${datum.name}: ${Number(datum.value).toFixed(2)} hrs`;
        },
      },
      title: {
        text: chartTitle || "Hours Summary",
        left: "center",
        textStyle: { fontSize: 16 },
      },
      grid: { left: 60, right: 30, bottom: 40, top: 60 },
      series: [
        {
          type: "bar",
          data: values,
          itemStyle: {
            borderRadius: 6,
            color: chartOrientation === "horizontal" ? "#06b6d4" : "#7c3aed",
          },
          barMaxWidth: 40,
        },
      ],
    };

    if (chartOrientation === "horizontal") {
      return {
        ...commonConfig,
        xAxis: { type: "value", name: "Hours" },
        yAxis: { type: "category", data: labels, inverse: false },
      };
    }

    return {
      ...commonConfig,
      xAxis: { type: "category", data: labels, axisLabel: { rotate: labels.length > 8 ? 45 : 0 } },
      yAxis: { type: "value", name: "Hours" },
    };
  }, [chartData, chartOrientation, chartTitle]);

  const chartHeight = useMemo(() => {
    if (!chartData.length) return 360;
    return chartOrientation === "horizontal"
      ? Math.min(640, Math.max(320, chartData.length * 32))
      : 420;
  }, [chartData, chartOrientation]);
  
  const filteredUsers =
  team === "all"
    ? userList
    : userList.filter((u) => u.teamIds?.includes(team));

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #3b82f6 0%, #7c3aed 50%, rgb(231, 58, 237) 100%)",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
    }}>
      <PageNavBar title="Report" backTo="/dashboard" />

      <TenantBanner />
      
      <Box 
        sx={{ 
          display: "flex", 
          justifyContent: "center", 
          mt: 6,
          px: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={3}
          sx={{ width: "100%", maxWidth: 1200 }}
        >
          <Box
            component="form"
            onSubmit={querySubmit}
            noValidate
            sx={{ 
              backgroundColor: "rgba(255, 255, 255, 0.98)",
              p: 4,
              borderRadius: 3,
              boxShadow: 3,
              width: "100%", 
              maxWidth: 500
            }}
          >
            <Stack spacing={3}>
              <TextField
                label="Start Date"
                type="date"
                name="startDate"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={handleDateChange(setStartDate)}
                required
              />
              <TextField
                label="End Date"
                type="date"
                name="endDate"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={handleDateChange(setEndDate)}
                required
              />
              <FormControl fullWidth>
                <InputLabel>Team</InputLabel>
                <Select
                  value={team}
                  label="Team"
                  onChange={(e) => {
                    setTeam(e.target.value);
                    setUser("all");
                  }}
                >
                  <MenuItem value="all">All Teams</MenuItem>
                
                  {teamList.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}   {/* ✅ SHOW NAME */}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>User</InputLabel>
                <Select 
                  name="user" 
                  value={user} 
                  label="User" 
                  onChange={handleChange}
                  required
                  disabled={userRole === "user" || !team}
                >
                  {(userRole === "admin" || userRole === "SuperAdmin") && (
                    <MenuItem value="all">All Users</MenuItem>
                  )}
                  {filteredUsers.map((entry) => (
                    <MenuItem key={entry.email} value={entry.email}>
                      {entry.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button type="submit" variant="contained" sx={{ mt: 1.5 }}>
                Generate Report
              </Button>
              {showDownloadButton && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >             
                    Download CSV file for selected dates
                  </Typography>
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={() => downloadCsv(csvPreview)}
                  > 
                    Download CSV
                  </Button>
                </>  
              )}
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              backgroundColor: "rgba(255, 255, 255, 0.98)",
              p: 4,
              borderRadius: 3,
              boxShadow: 3,
              minHeight: 420,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Visual Summary
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {chartData.length ? chartSubtitle : "Run a report to see a visualization of the logged hours."}
            </Typography>

            {chartData.length ? (
              <ReactECharts
                option={chartOptions || {}}
                style={{ height: chartHeight, width: "100%" }}
                notMerge
                lazyUpdate
              />
            ) : (
              <Box
                sx={{
                  border: "1px dashed rgba(0,0,0,0.2)",
                  borderRadius: 2,
                  p: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "text.secondary",
                  textAlign: "center",
                }}
              >
                Select a date range and user, then generate the report to preview the chart here.
              </Box>
            )}

            {chartData.length > 12 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                Showing horizontal bars automatically for larger result sets.
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            minWidth: 300,
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "auto"
          }}
        >
          <Typography variant="h6" component="h2">
            CSV Preview
          </Typography>
          <pre style={{ maxHeight: "60vh", overflow: "auto", whiteSpace: "pre-wrap" }}>
            {csvPreview}
          </pre>

          {showDownloadButton && (
            <Button
              onClick={() => downloadCsv(csvPreview)}
              variant="contained"
              sx={{ mt: 2 }}
            >
              Download CSV
            </Button>
          )}
        </Box>
      </Modal>
      <Footer />
    </Box>
  );
};

export default Report;
